import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Gauge, Fuel, Snowflake, DoorOpen, Star, ShieldCheck, Check, MapPin, ArrowLeft, ChevronRight, Crown, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { Button } from "@/components/ui/button";
import { DateField, fromISO } from "@/components/DateField";

const COUNTRY_LABELS = { martinique: "Martinique", guadeloupe: "Guadeloupe", "saint-martin": "Saint-Martin", "sint-maarten": "Sint Maarten" };

export default function VehicleDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const nav = useNavigate();
  const v3 = useFlags().v3_enabled;
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [reviews, setReviews] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [dates, setDates] = useState({ start: today, end: "" });

  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => nav("/search")); }, [id, nav]);
  useEffect(() => { api.get(`/vehicles/${id}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {}); }, [id]);

  if (!v) return <div className="min-h-[60vh] flex items-center justify-center text-canard font-body">{t("common.loading")}</div>;

  const days = dates.start && dates.end ? Math.max(1, Math.round((new Date(dates.end) - new Date(dates.start)) / 86400000)) : 0;
  const total = days * v.price_per_day;

  const goCheckout = () => {
    if (!dates.end) return;
    nav(`/checkout/${v.id}?start=${dates.start}&end=${dates.end}`);
  };

  const specs = [
    { icon: Users, label: `${v.seats} ${t("vehicle.seats")}` },
    { icon: Gauge, label: v.transmission },
    { icon: Fuel, label: v.fuel },
    { icon: DoorOpen, label: `${v.doors} portes` },
    { icon: Snowflake, label: v.air_conditioning ? t("vehicle.ac") : "—" },
    { icon: Calendar, label: v.year },
  ];

  return (
    <div className="section-sand min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <button onClick={() => nav(-1)} data-testid="back-button" className="flex items-center gap-1 text-canard font-semibold text-sm mb-5 hover:gap-2 transition-all">
          <ArrowLeft className="w-4 h-4" /> {t("checkout.back")}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden shadow-card bg-white">
              <img src={v.photos?.[active]} alt={v.model} className="w-full h-[380px] object-cover" data-testid="vehicle-main-image" />
              {v.photos?.length > 1 && (
                <div className="flex gap-2 p-3">
                  {v.photos.map((p, i) => (
                    <button key={p} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                      className={`w-20 h-16 rounded-xl overflow-hidden border-2 transition-all ${active === i ? "border-emeraude" : "border-transparent opacity-70"}`}>
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-border mt-6">
              <h2 className="font-heading font-bold text-xl text-ink mb-4">{t("vehicle.specs")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {specs.map((s) => (
                  <div key={s.label} className="flex items-center gap-2 text-muted2 font-semibold text-sm">
                    <s.icon className="w-5 h-5 text-canard" /> {s.label}
                  </div>
                ))}
              </div>
              {v.features?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {v.features.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 bg-emeraude/10 text-emeraude-dark px-3 py-1 rounded-full text-xs font-bold">
                      <Check className="w-3 h-3" /> {f}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-muted2 font-body mt-6 leading-relaxed">{v.description}</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-border mt-6" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl text-ink">{t("dash.reviewsTitle")}</h2>
                {v.rating > 0 && <div className="flex items-center gap-1 text-sm font-bold text-canard-dark"><Star className="w-4 h-4 fill-emeraude text-emeraude" /> {v.rating} · {v.reviews_count}</div>}
              </div>
              {reviews.length === 0 ? (
                <p className="text-muted2 font-body text-sm">{t("dash.noReviews")}</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((r) => (
                    <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-border last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-ink text-sm">{r.author_name}</span>
                          {v3 && (r.author_tier === "or" || r.author_tier === "argent") && (
                            <span data-testid={`review-tier-${r.id}`} className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${r.author_tier === "or" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-500"}`}><Crown className="w-3 h-3" /> {t("tier." + r.author_tier)}</span>
                          )}
                          {r.verified && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emeraude-dark bg-emeraude/10 px-2 py-0.5 rounded-full">✓ {t("dash.verifiedBadge")}</span>}
                        </div>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />)}
                        </div>
                      </div>
                      {r.comment && <p className="text-muted2 font-body text-sm mt-1">{r.comment}</p>}
                      {r.reply && (
                        <div className="mt-2 pl-3 border-l-2 border-emeraude/40 bg-emeraude/5 rounded-r-lg p-2">
                          <p className="text-xs font-bold text-emeraude-dark">{t("dash.loueurReply")}</p>
                          <p className="text-sm text-muted2 font-body">{r.reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-card border border-border mt-6">
              <h2 className="font-heading font-bold text-xl text-ink mb-3">{t("vehicle.conditions")}</h2>
              <ul className="space-y-2 text-sm text-muted2 font-body">
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emeraude shrink-0 mt-0.5" /> Caution (empreinte bancaire) de {v.deposit} € prise au comptoir lors du retrait.</li>
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emeraude shrink-0 mt-0.5" /> Permis de conduire obligatoire, vérifié au retrait du véhicule.</li>
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emeraude shrink-0 mt-0.5" /> Annulation gratuite jusqu'à 24h avant le départ (remboursement automatique).</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-card border border-border lg:sticky lg:top-24">
              <div className="flex items-start justify-between">
                <div>
                  <span className="inline-block bg-canard/10 text-canard-dark px-3 py-1 rounded-full text-xs font-bold mb-2">{v.category}</span>
                  <h1 className="font-heading font-extrabold text-2xl text-ink">{v.brand} {v.model}</h1>
                  <p className="text-xs text-muted2 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {COUNTRY_LABELS[v.country]} · {v.city}</p>
                </div>
                {v.rating > 0 && <div className="flex items-center gap-1 text-sm font-bold text-canard-dark"><Star className="w-4 h-4 fill-emeraude text-emeraude" /> {v.rating}</div>}
              </div>
              <p className="text-xs text-muted2 mt-2">Loueur : <span className="font-semibold text-canard-dark">{v.loueur_name}</span>
                {v.loueur_rating > 0 && <span className="inline-flex items-center gap-0.5 ml-1 text-emeraude-dark font-bold"><Star className="w-3 h-3 fill-emeraude text-emeraude" /> {v.loueur_rating} <span className="text-muted2 font-normal">({v.loueur_reviews_count})</span></span>}
              </p>
              {v.superloueur && (
                <span data-testid="superloueur-badge" className="inline-flex items-center gap-1 mt-2 bg-gradient-to-r from-emeraude to-canard text-white px-3 py-1 rounded-full text-xs font-extrabold shadow-cta">
                  <Crown className="w-3.5 h-3.5" /> {t("dash.superloueur")}
                </span>
              )}

              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-3xl font-heading font-extrabold text-canard-dark">{v.price_per_day} €</span>
                <span className="text-sm text-muted2 font-semibold">{t("vehicle.perDay")}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div>
                  <label className="text-xs font-bold uppercase text-muted2">{t("search.startDate")}</label>
                  <DateField value={dates.start} testid="detail-start-date" placeholder={t("search.startDate")}
                    disabled={{ before: fromISO(today) }}
                    onChange={(val) => setDates({ ...dates, start: val, end: dates.end && val && dates.end < val ? "" : dates.end })} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-muted2">{t("search.endDate")}</label>
                  <DateField value={dates.end} testid="detail-end-date" placeholder={t("search.endDate")}
                    disabled={{ before: fromISO(dates.start) || fromISO(today) }}
                    onChange={(val) => setDates({ ...dates, end: val })} />
                </div>
              </div>

              {days > 0 && (
                <div className="mt-5 bg-sand rounded-xl p-4 space-y-2 text-sm font-body">
                  <div className="flex justify-between"><span className="text-muted2">{v.price_per_day} € × {days} {t("checkout.days")}</span><span className="font-bold text-ink">{total.toFixed(2)} €</span></div>
                  <div className="flex justify-between text-xs text-muted2"><span>{t("vehicle.deposit")} (empreinte)</span><span>{v.deposit} €</span></div>
                  <div className="flex justify-between pt-2 border-t border-border"><span className="font-heading font-bold text-ink">{t("checkout.total")}</span><span className="font-heading font-extrabold text-canard-dark text-lg">{total.toFixed(2)} €</span></div>
                </div>
              )}

              <Button data-testid="book-now-button" disabled={!dates.end} onClick={goCheckout}
                className="w-full mt-5 h-12 rounded-full bg-emeraude hover:bg-emeraude-dark text-white font-heading font-bold shadow-cta disabled:opacity-50">
                {t("vehicle.book")} <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
              <p className="text-[11px] text-center text-muted2 mt-3 flex items-center justify-center gap-1"><Lock /> Paiement sécurisé Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lock() { return <ShieldCheck className="w-3 h-3" />; }
